import React, { useState } from 'react';
import { X, Upload, AlertTriangle, FileText } from 'lucide-react';

interface DisputeModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: any) => void;
    contractTitle: string;
}

export const DisputeModal: React.FC<DisputeModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    contractTitle,
}) => {
    const [reason, setReason] = useState('quality');
    const [expectation, setExpectation] = useState('full_refund');
    const [description, setDescription] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [isDragOver, setIsDragOver] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = () => {
        onSubmit({
            reason,
            expectation,
            description,
            files
        });
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        if (e.dataTransfer.files) {
            setFiles(Array.from(e.dataTransfer.files));
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setFiles(Array.from(e.target.files));
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg border border-slate-100 flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-slate-50">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center text-red-600">
                            <AlertTriangle size={20} />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-slate-900">发起申诉申请</h3>
                            <p className="text-xs text-slate-500 line-clamp-1">
                                针对合同: {contractTitle}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-600 transition-colors p-2 hover:bg-slate-50 rounded-full"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content - Scrollable */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Reason */}
                    <div className="space-y-3">
                        <label className="text-sm font-medium text-slate-700 block">
                            争议原因 <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="w-full h-11 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-50 transition-all"
                        >
                            <option value="quality">质量不符</option>
                            <option value="delay">逾期交付</option>
                            <option value="copyright">版权争议</option>
                            <option value="other">其他</option>
                        </select>
                    </div>

                    {/* Expectation */}
                    <div className="space-y-3">
                        <label className="text-sm font-medium text-slate-700 block">
                            期望诉求 <span className="text-red-500">*</span>
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {[
                                { value: 'full_refund', label: '全额退款' },
                                { value: 'partial_refund', label: '部分退款' },
                                { value: 'continue', label: '继续履行' },
                            ].map((option) => (
                                <label
                                    key={option.value}
                                    className={`
                    cursor-pointer flex items-center justify-center px-4 py-3 rounded-lg border text-sm font-medium transition-all
                    ${expectation === option.value
                                            ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm'
                                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'}
                  `}
                                >
                                    <input
                                        type="radio"
                                        name="expectation"
                                        value={option.value}
                                        checked={expectation === option.value}
                                        onChange={(e) => setExpectation(e.target.value)}
                                        className="hidden"
                                    />
                                    {option.label}
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Description */}
                    <div className="space-y-3">
                        <label className="text-sm font-medium text-slate-700 block">
                            问题描述 <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="请详细描述您遇到的问题..."
                            className="w-full h-32 p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-50 transition-all resize-none"
                        />
                    </div>

                    {/* Attachments */}
                    <div className="space-y-3">
                        <label className="text-sm font-medium text-slate-700 block">
                            上传凭证 (选填)
                        </label>
                        <div
                            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                            onDragLeave={() => setIsDragOver(false)}
                            onDrop={handleDrop}
                            className={`
                relative border-2 border-dashed rounded-xl p-6 transition-all text-center
                ${isDragOver ? 'border-blue-400 bg-blue-50' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'}
              `}
                        >
                            <input
                                type="file"
                                multiple
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                onChange={handleFileChange}
                            />
                            <div className="flex flex-col items-center justify-center gap-2 pointer-events-none">
                                <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center text-slate-400">
                                    <Upload size={20} />
                                </div>
                                <p className="text-sm font-medium text-slate-700">
                                    点击或拖拽上传截图/文件
                                </p>
                                <p className="text-xs text-slate-400">
                                    支持 JPG, PNG, PDF (最大 10MB)
                                </p>
                            </div>
                        </div>

                        {/* File List */}
                        {files.length > 0 && (
                            <div className="space-y-2 mt-3">
                                {files.map((file, index) => (
                                    <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                                        <div className="w-8 h-8 rounded bg-white flex items-center justify-center text-blue-500 shadow-sm">
                                            <FileText size={16} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-slate-700 line-clamp-1">{file.name}</p>
                                            <p className="text-xs text-slate-400">{(file.size / 1024).toFixed(1)} KB</p>
                                        </div>
                                        <button
                                            onClick={() => setFiles(files.filter((_, i) => i !== index))}
                                            className="text-slate-400 hover:text-red-500 transition-colors p-1"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-5 border-t border-slate-50 bg-slate-50 rounded-b-xl flex items-center justify-end gap-3 glass-footer">
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-800 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
                    >
                        取消
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!description.trim()}
                        className="px-5 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 active:bg-red-800 rounded-lg shadow-sm hover:shadow transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        提交申请
                    </button>
                </div>
            </div>
        </div>
    );
};
